import req from '@sportninja/common/api/request'
import { useCallback, useState } from 'react'
import type { TeamHistoryFields } from '../features/registration/teamHistory/types'

/**
 * A team from either registration teams endpoint. The team-official endpoint
 * also carries the history fields (`TeamHistoryFields`); the player endpoint
 * does not.
 */
export type Team = Omit<TeamHistoryFields, 'player_count'> & {
  uid: string
  /**
   * The roster row this team registers through. Sent on at `team_register`;
   * a DIFFERENT namespace from the team id, so never match a link against it.
   */
  team_player_roster_id?: string | null
  name: string
  abbreviation: string | null
  email: string | null
  website: string | null
  phone: string | null
  sport_id: string
  image: string | null
  /**
   * Player endpoint: registered players against `max_players_per_team`.
   * Team-official endpoint: players on the most recent roster (spares
   * excluded), `null` for a team that never played.
   */
  player_count: number | null
  max_players_per_team: number
}

export type Role = 'player' | 'team-official' | null

export const useUserRegistrationTeams = (
  registrationId: string,
  role: Role
) => {
  const [data, setData] = useState<Team[] | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async () => {
    if (!registrationId || !role) {
      return
    }
    setLoading(true)
    setError(null)
    try {
      const endpoint =
        role === 'player'
          ? `/registration/${registrationId}/player/teams`
          : `/registration/${registrationId}/team-official/teams`
      const response = await req(endpoint)
      setData(response.data)
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching registration teams')
      )
    } finally {
      setLoading(false)
    }
  }, [registrationId, role])

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  }
}
